import { useState, useEffect } from 'react';
import { RequireAssessmentsAndStudents } from '../components/DependencyGate';
import {
  Datagrid,
  List,
  Create,
  Edit,
  TabbedForm,
  FormTab,
  TextField,
  TextInput,
  ReferenceField,
  SimpleFormIterator,
  ArrayInput,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  Show,
  TabbedShowLayout,
  Tab,
  FunctionField,
  DateTimeInput,
  NumberInput,
  useDataProvider,
  useNotify,
  usePermissions,
  useRecordContext,
  useRedirect,
  useGetList,
  required,
  Link,
  SimpleForm,
} from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import { Alert, Box, Typography, Divider } from '@mui/material';
import { attemptAnnotationTransform, attemptCreateSuccessMessage, attemptTransform, teamSizeHelperText, teamSizeViolation, teamEligible, teamOptionText } from './attempts-helpers';
import {
  AssessmentField,
  AssessmentSelect,
  EventField,
  EventSelect,
  FullNameField,
  getFullName,
  StudentField,
  StudentSelect,
} from '../components/selects';
import { useAssessmentIds } from '../utils/useCourseIds';
import {
  hasRole,
  type Permissions,
  type Attempt as AttemptType,
  type MeasurementDef,
  type MeasurementResult,
  type Event as EventType,
  type Team,
  type TeamSize,
} from '../types';
import { PhysiologicalContextPanel } from './wearables';

// ---------------------------------------------------------------------------
// Compound-unit result display
// ---------------------------------------------------------------------------

function formatNormalizedResult(normalized: number, mDef: MeasurementDef): string {
  const { unit_scheme, kind } = mDef;
  if (kind === 'pass_fail') return normalized === 1 ? 'Pass' : 'Fail';
  if (!unit_scheme?.parts?.length) return `${normalized} ${unit_scheme?.base_unit ?? ''}`;

  let remaining = normalized;
  const parts: string[] = [];
  for (const part of unit_scheme.parts) {
    const val = Math.floor(remaining / part.factor);
    parts.push(`${val} ${part.label}`);
    remaining -= val * part.factor;
  }
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// MeasurementResultsField — shown in Show view
// ---------------------------------------------------------------------------

const MeasurementResultsField = () => {
  const record = useRecordContext<AttemptType>();
  const dataProvider = useDataProvider();
  const [eventMeasurements, setEventMeasurements] = useState<MeasurementDef[]>([]);

  useEffect(() => {
    if (!record?.event_id) return;
    dataProvider
      .getOne('events', { id: record.event_id })
      .then(({ data }: { data: EventType }) => {
        setEventMeasurements(data.measurements ?? []);
      })
      .catch(() => {});
  }, [record?.event_id, dataProvider]);

  if (!record?.measurement_results?.length) {
    // Legacy display — scores array
    if (record?.scores?.length) {
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Scores (legacy)</Typography>
          {record.scores.map((item, index) => (
            <Typography key={index} variant="body2">
              Behavior {index + 1}: {item === 100 ? 'PASS' : 'FAIL'}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Measurement Results</Typography>
      {record.measurement_results.map((mr: MeasurementResult, idx: number) => {
        const mDef = eventMeasurements.find((m) => m.measurement_id === mr.measurement_id);
        const label = mDef?.name ?? mr.measurement_id;
        const display = mDef
          ? formatNormalizedResult(mr.raw_normalized, mDef)
          : `${mr.raw_normalized}`;
        return (
          <Box key={idx} mb={1}>
            <Typography variant="body2">
              <strong>{label}:</strong> {display}
              {mr.points != null && (
                <span style={{ marginLeft: 12, color: '#1976d2' }}>
                  → <strong>{mr.points} pts</strong>
                  {mr.score_table_id ? (
                    <span style={{ fontSize: '0.75em', opacity: 0.7 }}>
                      {' '}(
                      <Link to={`/score_tables/${mr.score_table_id}/show`}>
                        table v{(mr.score_table_version ?? mr.score_table_id).slice(0, 10)}
                      </Link>
                      )
                    </span>
                  ) : mr.score_table_version ? (
                    <span style={{ fontSize: '0.75em', opacity: 0.7 }}>
                      {' '}(table v{mr.score_table_version.slice(0, 10)})
                    </span>
                  ) : null}
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// MeasurementResultsInput — compound-unit entry at grading time
// ---------------------------------------------------------------------------

const MeasurementResultsInput = () => {
  const eventId = useWatch({ name: 'event_id' });
  const dataProvider = useDataProvider();
  const [measurements, setMeasurements] = useState<MeasurementDef[]>([]);

  useEffect(() => {
    if (!eventId) return;
    dataProvider
      .getOne('events', { id: eventId })
      .then(({ data }: { data: EventType }) => {
        setMeasurements(data.measurements ?? []);
      })
      .catch(() => {});
  }, [eventId, dataProvider]);

  if (!measurements.length) return null;

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>
        Measurement Results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter results for each measurement. Values are stored normalized in the base unit.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Measurements are defined by the Event. Go to Configure, Events to change them.
      </Alert>
      <ArrayInput
        label=""
        source="measurement_results"
        defaultValue={measurements.map((m) => ({
          measurement_id: m.measurement_id,
          raw_parts: m.unit_scheme.parts.map(() => 0),
          raw_normalized: 0,
        }))}
      >
        <SimpleFormIterator disableAdd disableRemove disableReordering>
          <TextInput
            source="measurement_id"
            label="Measurement ID"
            disabled
            sx={{ display: 'none' }}
          />
          {measurements.map((mDef) => (
            <MeasurementPartInputs key={mDef.measurement_id} mDef={mDef} />
          ))}
        </SimpleFormIterator>
      </ArrayInput>
    </Box>
  );
};

const MeasurementPartInputs = ({ mDef }: { mDef: MeasurementDef }) => {
  const { name, kind, unit_scheme } = mDef;

  if (kind === 'pass_fail') {
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>{name}</Typography>
        <SelectInput
          source="raw_normalized"
          label={name}
          choices={[
            { id: 1, name: 'Pass' },
            { id: 0, name: 'Fail' },
          ]}
          defaultValue={0}
        />
      </Box>
    );
  }

  if (!unit_scheme?.parts?.length) {
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {name} ({unit_scheme?.base_unit ?? 'units'})
        </Typography>
        <NumberInput source="raw_normalized" label={unit_scheme?.base_unit ?? 'Value'} min={0} />
      </Box>
    );
  }

  // Compound unit — render one NumberInput per part, with instructions
  return (
    <Box mb={2}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        {name} ({unit_scheme.parts.map((p) => p.label).join(' : ')})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Enter each part. The normalized value will be computed as:{' '}
        {unit_scheme.parts.map((p) => `${p.label}×${p.factor}`).join(' + ')}
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap">
        {unit_scheme.parts.map((part, i) => (
          <NumberInput
            key={i}
            source={`raw_parts.${i}`}
            label={`${part.label}${part.max !== undefined ? ` (0–${part.max})` : ''}`}
            min={0}
            max={part.max}
            style={{ width: 120 }}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Normalized value (base unit) is computed server-side from parts above. Set raw_normalized
        below only if entering pre-computed values.
      </Typography>
      <NumberInput source="raw_normalized" label="Normalized value (base unit, computed)" min={0} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

const AttemptListInner = () => {
  const assessment_ids = useAssessmentIds();
  const dataProvider = useDataProvider();
  const [groups, setGroups] = useState<any[]>([]);
  const { permissions } = usePermissions<Permissions>();

  useEffect(() => {
    dataProvider
      .getList('student_groups', {
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
        filter: {},
      })
      .then(({ data }) => setGroups(data))
      .catch(() => {});
  }, [dataProvider]);

  return (
    <List
      exporter={false}
      filter={assessment_ids ? { assessment_id: assessment_ids } : undefined}
      filters={[
        <AssessmentSelect key="assessment" alwaysOn />,
        <EventSelect key="event" alwaysOn />,
        <SelectInput
          key="class"
          alwaysOn
          label="Class"
          source="student_group_id"
          choices={groups.map((group) => ({
            id: group.id,
            name: group.name,
          }))}
        />,
        <StudentSelect key="student" alwaysOn />,
      ]}
      perPage={25}
    >
      <Datagrid rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}>
        <ReferenceField label="Event" source="event_id" reference="events">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Student" source="student_id" reference="students">
          <FullNameField />
        </ReferenceField>
        <FunctionField
          label="Grade / Points"
          render={(record: AttemptType) => {
            // Show points if available
            const totalPts = record?.measurement_results
              ?.reduce((sum: number, mr: MeasurementResult) => sum + (mr.points ?? 0), 0);
            if (totalPts != null && totalPts > 0) return `${totalPts} pts`;
            // Fall back to legacy pass/fail
            if (record?.scores?.every((s) => s === 100)) return 'Pass';
            if (record?.scores?.some((s) => s !== undefined)) return 'Fail';
            return '—';
          }}
        />
      </Datagrid>
    </List>
  );
};

const AttemptList = () => (
  <RequireAssessmentsAndStudents>
    <AttemptListInner />
  </RequireAssessmentsAndStudents>
);

// ---------------------------------------------------------------------------
// Show
// ---------------------------------------------------------------------------

const PhysioTab = () => {
  const record = useRecordContext<AttemptType>();
  return <PhysiologicalContextPanel biometricContext={record?.biometric_context} />;
};

const AttemptShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Summary">
        <EventField />
        <AssessmentField />
        <StudentField />
        <TextField label="Notes" source="notes" />
      </Tab>
      <Tab label="Results">
        <MeasurementResultsField />
      </Tab>
      <Tab label="Physiological Context">
        <PhysioTab />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

// ---------------------------------------------------------------------------
// Create / Edit
// ---------------------------------------------------------------------------

const AggregateStudentPicker = ({ memberIds }: { memberIds: string[] }) => {
  const dataProvider = useDataProvider();
  const [choices, setChoices] = useState<any[]>([]);

  useEffect(() => {
    if (!memberIds.length) return;
    dataProvider
      .getMany('students', { ids: memberIds })
      .then(({ data }) => setChoices(data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIds.join(','), dataProvider]);

  return (
    <AutocompleteInput
      source="student_id"
      label="Team Member"
      choices={choices}
      optionText={getFullName}
      helperText="Only members of the selected team are shown."
    />
  );
};

const TeamAttemptSection = () => {
  const eventId = useWatch({ name: 'event_id' });
  const teamId = useWatch({ name: 'team_id' });
  const dataProvider = useDataProvider();
  const [event, setEvent] = useState<(EventType & { participation?: string; team_size?: TeamSize; team_scoring?: string }) | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const { setValue } = useFormContext();

  const { data: allTeams = [] } = useGetList<Team>('teams', {
    filter: { status: 'active' },
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      setValue('_participation', undefined);
      setValue('_teamScoring', undefined);
      return;
    }
    dataProvider
      .getOne('events', { id: eventId })
      .then(({ data }) => {
        const ev = data as any;
        setEvent(ev);
        setValue('_participation', ev.participation ?? 'individual');
        setValue('_teamScoring', ev.team_scoring);
      })
      .catch(() => {});
  }, [eventId, dataProvider, setValue]);

  useEffect(() => {
    if (!teamId) { setTeam(null); return; }
    dataProvider
      .getOne('teams', { id: teamId })
      .then(({ data }) => setTeam(data as Team))
      .catch(() => {});
  }, [teamId, dataProvider]);

  if (!event || (event as any).participation !== 'team') return null;

  const sizeRule = (event as any).team_size as TeamSize | undefined;
  const eligibleTeams = allTeams.filter((t) => teamEligible(t, sizeRule));
  const warning = teamSizeViolation(team, sizeRule);
  const memberCount = team?.member_ids?.length ?? 0;

  return (
    <>
      <AutocompleteInput
        source="team_id"
        label="Team"
        choices={eligibleTeams}
        optionText={(t: any) => teamOptionText(t as Team, sizeRule)}
        validate={required('A team is required for team events')}
        helperText={
          sizeRule
            ? `Only teams matching this event's size rule are shown. ${teamSizeHelperText(sizeRule)}`
            : 'Select an active team.'
        }
      />
      {warning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {warning}
        </Alert>
      )}
      {(event as any).team_scoring === 'single_result' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This result will be recorded for all {memberCount || '?'} team members.
        </Typography>
      )}
      {(event as any).team_scoring === 'aggregate' && team && (
        <AggregateStudentPicker memberIds={team.member_ids ?? []} />
      )}
    </>
  );
};

const AttemptCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  return (
    <Create
      transform={attemptTransform}
      mutationOptions={{
        onSuccess: (data: any) => {
          notify(attemptCreateSuccessMessage(data), { type: 'success' });
          redirect('list', 'attempts');
        },
      }}
    >
      <AttemptForm />
    </Create>
  );
};

const AttemptEdit = () => (
  <Edit mutationMode="pessimistic" transform={attemptAnnotationTransform}>
    <SimpleForm warnWhenUnsavedChanges>
      <Alert severity="info" sx={{ mb: 2 }}>
        Accepted measurement and identity fields are immutable. This form records an annotation only.
      </Alert>
      <TextInput fullWidth label="Annotation" source="notes" validate={required()} />
      <TextInput
        fullWidth
        label="Correction reason"
        source="correction_reason"
        validate={required()}
        helperText="Required for versioned capture evidence."
      />
      <NumberInput
        source="expected_version"
        label="Expected record version"
        helperText="Leave blank to use the version loaded with this record."
      />
    </SimpleForm>
  </Edit>
);

const IndividualStudentPicker = () => {
  const participation = useWatch({ name: '_participation' });
  if (participation === 'team') return null;
  return (
    <ReferenceInput
      perPage={100}
      label="Student"
      source="student_id"
      sort={{ field: 'first_name', order: 'ASC' }}
      reference="students"
    >
      <AutocompleteInput optionText={getFullName} />
    </ReferenceInput>
  );
};

const AttemptForm = () => (
  <TabbedForm warnWhenUnsavedChanges>
    <FormTab label="Details">
      <ReferenceInput
        perPage={100}
        label="Event"
        source="event_id"
        sort={{ field: 'name', order: 'ASC' }}
        reference="events"
      >
        <AutocompleteInput optionText="name" validate={required()} />
      </ReferenceInput>
      <ReferenceInput
        perPage={100}
        label="Assessment"
        source="assessment_id"
        sort={{ field: 'name', order: 'ASC' }}
        reference="assessments"
      >
        <AutocompleteInput optionText="name" validate={required()} />
      </ReferenceInput>
      <TeamAttemptSection />
      <IndividualStudentPicker />
      <TextInput fullWidth label="Notes" source="notes" />
      <NumberInput fullWidth source="attempt_index" />
      <DateTimeInput fullWidth source="attempted_at" />
    </FormTab>

    <FormTab label="Measurement Results">
      <MeasurementResultsInput />
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Legacy scores (pass/fail array — used only when measurements are not defined)
      </Typography>
      <ArrayInput label="Legacy Scores" source="scores">
        <SimpleFormIterator>
          <TextInput fullWidth label="Score" source="" />
        </SimpleFormIterator>
      </ArrayInput>
    </FormTab>
  </TabbedForm>
);

export const attempts = {
  list: AttemptList,
  show: AttemptShow,
  create: AttemptCreate,
  edit: AttemptEdit,
};
